<script lang="ts">
	// Stores
		import { getDeviceStatus, getDeviceSettings } from 'src/stores/device-runtime/status-selectors';

	// Services and configs
	import { devices_config } from 'src/configs/devices';
	import { getDeviceChartsConfig } from 'src/helpers/statistics/config/device_chart_config';

	// Constants
	import { GAS_SENSOR_TYPES } from 'src/constants';

	// Helpers
	import { checkIsCriticalEvent } from 'src/helpers/device_data_helpers';
	import { capFirstChar } from 'src/helpers/utilities';
	import {
		extractGen1MainLabel,
		getGen1DeviceStatuses,
		createGen1StatusLabels,
	} from 'src/components/Sidepanel/SidePanelLayoutProvider/SidePanelDeviceLayout/Devices/Shelly/Gen1/gen1-main-helpers';

	// Design System Components
	import { ParameterCardV2 } from '@Shelly/shelly-design-system';
	// Helpers
	import { mapIconToThin } from 'src/helpers/new-layout-helpers';

	// Components
	import DeviceViewStateWrapper from 'src/components/Sidepanel/DeviceViews/DeviceViewStateWrapper.svelte';
	import ControlGasSensorMute from 'src/components/Controls/Single/ControlGasSensorMute.svelte';
	import DeviceNotificationLabel from 'src/components/Sidepanel/DeviceNotificationLabel.svelte';
	import { useNavigateToHistoryStatisticsTab } from 'src/components/Sidepanel/useNavigateToHistoryStatisticsTab';

	// Types
	import type { Device } from 'src/types/Device.ts';

	import DeviceLabelList from '../DeviceLabelList.svelte';

	/**
	 * Main control tab for the sensor devices sidepanel drawer.
	 *
	 * Responsibilities:
	 * - displays label list with sensor statuses (temperature, battery)
	 * - shows device charts when configured
	 * - shows sensor status as ParameterCard
	 * - shows firmware incompatibility warnings
	 *
	 * @param {Device} device - The device for which the information is displayed.
	 */
	export let device: Device;
	const { navigateToHistoryStatisticsTab } = useNavigateToHistoryStatisticsTab();

	// Helper function for device type check
	$: isGasSensor = GAS_SENSOR_TYPES.includes(device.type as (typeof GAS_SENSOR_TYPES)[number]);

	$: deviceConfig = devices_config?.[device?.type];

	$: statusStore = getDeviceStatus(device?.id);
	$: status = $statusStore;
	$: settingsStore = getDeviceSettings(device?.id);
	$: settings = $settingsStore;
	$: deviceChartsConfig = device ? getDeviceChartsConfig(device) : [];

	$: _statuses = getGen1DeviceStatuses(status, device, settings, deviceConfig);
	$: labelList = createGen1StatusLabels(_statuses);

	$: mainLabel = extractGen1MainLabel(_statuses);

	const handleMainLabelClick = () => {
		if (deviceChartsConfig.length) {
			navigateToHistoryStatisticsTab();
		}
	};
</script>

<DeviceViewStateWrapper {device}>
	<div slot="staticTop">
		<DeviceLabelList {labelList} />
		<DeviceNotificationLabel deviceId={device?.id} />
	</div>
	<div class="flex-full-height justify-content-end" slot="staticBottom">
		<div>
			{#if mainLabel}
				{@const isCriticalEvent = checkIsCriticalEvent(mainLabel.value)}
				<ParameterCardV2
					class={deviceChartsConfig.length > 0 ? 'mb-s cursor-pointer' : 'mb-s'}
					icon={mapIconToThin(mainLabel.icon)}
					title={capFirstChar(mainLabel.value)}
					description={capFirstChar(mainLabel.translation)}
					backgroundColor={isCriticalEvent ? '--color-danger-10' : '--color-0'}
					isFocusable={deviceChartsConfig.length > 0}
					on:click={handleMainLabelClick}
				/>
			{/if}
		</div>
		{#if isGasSensor}
			<ControlGasSensorMute {device} />
		{/if}
	</div>
</DeviceViewStateWrapper>
